import { useState, useEffect } from "react";
import { useAuth, api } from "@/App";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Gift, Star, Loader2, CheckCircle, Sparkles, TrendingUp, Tag } from "lucide-react";
import { toast } from "sonner";

const Rewards = () => {
  const { loyalty, refreshLoyalty } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [successModal, setSuccessModal] = useState(null);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await api.get("/rewards");
      setRewards(response.data);
    } catch (error) {
      toast.error("Failed to load rewards");
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!selectedReward) return;
    
    setRedeeming(true);
    try {
      const response = await api.post(`/rewards/redeem/${selectedReward.id}`);
      setSuccessModal(response.data.voucher);
      setSelectedReward(null);
      await refreshLoyalty();
      toast.success("Reward redeemed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem reward");
    } finally {
      setRedeeming(false);
    }
  };

  const getRewardIcon = (type) => {
    switch(type) {
      case 'discount': return <Tag size={24} />;
      case 'upgrade': return <TrendingUp size={24} />;
      case 'free_service': return <Sparkles size={24} />;
      default: return <Gift size={24} />;
    }
  };

  const getRewardTypeLabel = (type) => {
    switch(type) {
      case 'discount': return 'Discount';
      case 'upgrade': return 'Upgrade';
      case 'free_service': return 'Complimentary';
      default: return type;
    }
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="h-72 rounded-xl animate-shimmer" />
          ))}
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-display font-medium text-white mb-2">Rewards Catalog</h1>
            <p className="text-[#5A5A5A]">Redeem your miles for exclusive privileges</p>
          </div>
          
          {/* Balance Card */}
          <div className="glass-gold rounded-xl p-5 flex items-center gap-5 min-w-[280px]">
            <div className="w-12 h-12 rounded-lg bg-[#C9A962]/10 border border-[#C9A962]/20 flex items-center justify-center">
              <Star className="text-[#C9A962]" size={22} />
            </div>
            <div>
              <p className="text-[#9A9A9A] text-xs uppercase tracking-wider">Your Balance</p>
              <p className="text-2xl font-display font-medium text-white">
                {loyalty?.current_points?.toLocaleString() || 0} <span className="text-[#C9A962] text-lg">miles</span>
              </p>
            </div>
          </div>
        </div>

        {/* Rewards Grid */}
        {rewards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rewards.map((reward) => {
              const canAfford = (loyalty?.current_points || 0) >= reward.cost_points;
              
              return (
                <Card 
                  key={reward.id} 
                  className={`luxury-card luxury-card-hover border-0 overflow-hidden ${
                    !canAfford ? 'opacity-60' : ''
                  }`}
                >
                  <CardContent className="p-0">
                    {/* Top accent line */}
                    <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent" />
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-5">
                        <div className="icon-luxury">
                          {getRewardIcon(reward.reward_type)}
                        </div>
                        <span className="badge badge-gold text-[10px]">
                          {getRewardTypeLabel(reward.reward_type)}
                        </span>
                      </div>
                      
                      <h3 className="text-white font-display text-lg mb-2">{reward.name}</h3>
                      <p className="text-[#5A5A5A] text-sm mb-5 line-clamp-2 min-h-[40px]">
                        {reward.description}
                      </p>
                      
                      {reward.value_cents > 0 && (
                        <p className="text-[#5CB85C] text-sm mb-5">
                          Value: ${(reward.value_cents / 100).toFixed(2)}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between pt-5 border-t border-[#252525]">
                        <div>
                          <p className="text-[#C9A962] font-display text-2xl font-medium">
                            {reward.cost_points.toLocaleString()}
                          </p>
                          <p className="text-[#5A5A5A] text-xs uppercase tracking-wider">miles</p>
                        </div>
                        <Button
                          onClick={() => setSelectedReward(reward)}
                          disabled={!canAfford}
                          className={canAfford 
                            ? "bg-gradient-to-r from-[#C9A962] to-[#A68B4B] text-[#0A0A0A] font-semibold text-xs tracking-wider uppercase hover:opacity-90" 
                            : "bg-[#252525] text-[#5A5A5A] cursor-not-allowed"
                          }
                          data-testid={`redeem-btn-${reward.id}`}
                        >
                          {canAfford ? 'Redeem' : 'Insufficient'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-6">
              <Gift size={36} className="text-[#5A5A5A]" />
            </div>
            <h3 className="text-white font-display text-xl mb-2">No Rewards Available</h3>
            <p className="text-[#5A5A5A]">Check back soon for exclusive rewards</p>
          </div>
        )}

        {/* Confirm Redeem Dialog */}
        <Dialog open={!!selectedReward} onOpenChange={() => setSelectedReward(null)}>
          <DialogContent className="bg-[#111111] border-[#252525] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Confirm Redemption</DialogTitle>
              <DialogDescription className="text-[#5A5A5A]">
                You are about to redeem this reward
              </DialogDescription>
            </DialogHeader>
            
            {selectedReward && (
              <div className="py-6">
                <div className="glass rounded-xl p-5 mb-6">
                  <h4 className="text-white font-medium mb-2">{selectedReward.name}</h4>
                  <p className="text-[#5A5A5A] text-sm">{selectedReward.description}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-[#252525]">
                    <span className="text-[#9A9A9A]">Cost</span>
                    <span className="text-[#C9A962] font-display text-lg">
                      {selectedReward.cost_points.toLocaleString()} miles
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#252525]">
                    <span className="text-[#9A9A9A]">Your Balance</span>
                    <span className="text-white font-medium">
                      {loyalty?.current_points?.toLocaleString() || 0} miles
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-[#9A9A9A]">After Redemption</span>
                    <span className="text-[#5CB85C] font-display text-lg">
                      {((loyalty?.current_points || 0) - selectedReward.cost_points).toLocaleString()} miles
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setSelectedReward(null)}
                className="border-[#252525] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRedeem}
                disabled={redeeming}
                className="bg-gradient-to-r from-[#C9A962] to-[#A68B4B] text-[#0A0A0A] font-semibold"
                data-testid="confirm-redeem-btn"
              >
                {redeeming ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                ) : (
                  'Confirm Redemption'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Success Modal */}
        <Dialog open={!!successModal} onOpenChange={() => setSuccessModal(null)}>
          <DialogContent className="bg-[#111111] border-[#252525] text-white text-center max-w-md">
            <div className="py-8">
              <div className="w-20 h-20 rounded-full bg-[#5CB85C]/10 border border-[#5CB85C]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-[#5CB85C]" size={36} />
              </div>
              <h3 className="text-2xl font-display font-medium mb-2">Congratulations</h3>
              <p className="text-[#5A5A5A] mb-8">Your voucher has been issued</p>
              
              {successModal && (
                <div className="voucher-code mb-6" data-testid="new-voucher-code">
                  {successModal.code}
                </div>
              )}
              
              <p className="text-[#5A5A5A] text-sm">
                Valid until {successModal && new Date(successModal.expires_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
            
            <Button
              onClick={() => setSuccessModal(null)}
              className="w-full bg-gradient-to-r from-[#C9A962] to-[#A68B4B] text-[#0A0A0A] font-semibold"
            >
              Done
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </CustomerLayout>
  );
};

export default Rewards;
